import { Weight } from "lucide-react";
import { useState } from "react";
import { TextStyleSelect } from "./TextStyleSelect";
import {
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultSizeStyle,
  ArrowShapeArrowheadStartStyle,
  ArrowShapeArrowheadEndStyle,
  ArrowShapeKindStyle,
  toRichText,
} from "@tldraw/tlschema";
import type { WorkspaceModel } from "./useWorkspace";
import {
  drawingDefaults,
  nativeColor,
  updateDrawingDefaults,
  type DrawingDefaults,
} from "./drawing-defaults";
import { scaleSource, sourceMeters, sourceName } from "./source-scale";
import { unitsPerMeter } from "../shared/mark-presentation";
const nativeStyles = {
  color: DefaultColorStyle,
  size: DefaultSizeStyle,
  dash: DefaultDashStyle,
  fill: DefaultFillStyle,
  start: ArrowShapeArrowheadStartStyle,
  end: ArrowShapeArrowheadEndStyle,
  arrow: ArrowShapeKindStyle,
};
const nativeKeys = {
  start: "arrowheadStart",
  end: "arrowheadEnd",
  arrow: "kind",
} as const;
const drawingTools = [
  "draw",
  "line",
  "arrow",
  "geo",
  "polygon",
  "cloud",
  "highlight",
  "dimension",
  "area",
  "angle",
];
export function DrawingControls({
  model,
  compact = false,
}: {
  model: WorkspaceModel;
  compact?: boolean;
}) {
  const { editor, tool } = model;
  const selected = tool === "select" ? model.selected : [];
  const [, refresh] = useState(0);
  if (!model.canEdit || selected.some((s) => editor.isShapeOrAncestorLocked(s)))
    return null;
  const supported = selected.length
    ? selected.every((s) =>
        ["draw", "geo", "arrow", "highlight", "mark"].includes(s.type),
      )
    : drawingTools.includes(tool);
  if (!supported) return null;
  const allMarks = selected.length
    ? selected.every((s) => s.type === "mark")
    : ["line", "polygon", "dimension", "area", "angle"].includes(tool);
  if (selected.some((s) => s.type === "mark") && !allMarks) return null;
  const kind =
    selected.length === 1
      ? selected[0].type === "mark"
        ? selected[0].meta.polygon
          ? "polygon"
          : selected[0].props.kind
        : selected[0].type
      : tool;
  const defaults = drawingDefaults(editor, kind);
  const value = (key: keyof DrawingDefaults): string | number | boolean => {
    if (!selected.length) return defaults[key];
    const values = selected.map((s) =>
      key === "opacity"
        ? s.opacity
        : key === "color" || key === "weight"
          ? (s.props as unknown as Record<string, unknown>)[key]
          : allMarks
            ? (s.meta[key] ?? defaults[key])
            : (s.props as unknown as Record<string, unknown>)[
                nativeKeys[key as keyof typeof nativeKeys] || key
              ],
    );
    return values.every((v) => v === values[0])
      ? (values[0] as string | number | boolean)
      : "";
  };
  const set = (key: keyof DrawingDefaults, next: string | number | boolean) => {
    if (!selected.length) updateDrawingDefaults(editor, kind, { [key]: next });
    else {
      editor.markHistoryStoppingPoint("Drawing style");
      if (key === "opacity")
        editor.updateShapes(
          selected.map((s) => ({
            id: s.id,
            type: s.type,
            opacity: Number(next),
          })),
        );
      else if (allMarks)
        editor.updateShapes(
          selected
            .filter((s) => s.type === "mark")
            .map((s) => ({
              id: s.id,
              type: s.type,
              ...(["color", "weight"].includes(key)
                ? { props: { [key]: next } }
                : { meta: { ...s.meta, [key]: next } }),
            })),
        );
      else if (key in nativeStyles) {
        const style = nativeStyles[key as keyof typeof nativeStyles];
        // The menu values are the enum values of the matching tldraw style.
        editor.setStyleForSelectedShapes(
          style as typeof DefaultDashStyle,
          (key === "color" ? nativeColor(String(next)) : next) as "solid",
        );
      }
    }
    refresh((n) => n + 1);
  };
  const select = (
    key: keyof DrawingDefaults,
    label: string,
    options: readonly string[],
  ) => (
    <label key={key}>
      {label}
      <select
        aria-label={label}
        value={String(value(key))}
        onChange={(e) => set(key, e.target.value)}
      >
        <option value="" disabled>
          Mixed
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {key === "unit"
              ? o
              : (
                  {
                    s: "Thin",
                    m: "Medium",
                    l: "Thick",
                    xl: "Extra thick",
                    semi: "Tint",
                    arc: "Straight / curved",
                    tick: "Diagonal tick",
                    bar: "Bar",
                    arrow: "Arrow",
                    dot: "Dot",
                    none: "None",
                    solid: "Solid",
                    dashed: "Dashed",
                    dotted: "Dotted",
                    pattern: "Pattern",
                    elbow: "Elbow",
                    diagonal: "Diagonal",
                    cross: "Crosshatch",
                  } as Record<string, string>
                )[o] || o}
          </option>
        ))}
      </select>
    </label>
  );
  const number = (
    key: keyof DrawingDefaults,
    label: string,
    min: number,
    max: number,
    step = 1,
    multiplier = 1,
  ) => (
    <label key={key}>
      {label}
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={step}
        value={
          typeof value(key) === "number" ? Number(value(key)) * multiplier : ""
        }
        placeholder="Mixed"
        onChange={(e) => {
          if (e.target.value !== "" && Number.isFinite(e.target.valueAsNumber))
            set(
              key,
              Math.max(min, Math.min(max, e.target.valueAsNumber)) / multiplier,
            );
        }}
      />
    </label>
  );
  const source = scaleSource(editor, model.selected[0]) || model.active;
  const area = allMarks && ["area", "polygon"].includes(kind),
    measurement = allMarks && ["dimension", "area", "angle"].includes(kind);
  const colors = [
    ["#d65331", "Terracotta"],
    ["#25343b", "Charcoal"],
    ["#517691", "Blue"],
    ["#657a4c", "Green"],
    ["#9b6589", "Violet"],
  ];
  const colorValue = String(value("color"));
  const currentColor = colors.find(
    ([hex]) => hex === colorValue || nativeColor(hex) === colorValue,
  )?.[0];
  const colour = (
    <TextStyleSelect
      label="Drawing colour"
      title="Drawing colour"
      className="drawing-color"
      value={colorValue || "mixed"}
      decoration={
        <span
          className="text-color-swatch"
          style={{
            background:
              currentColor ||
              "conic-gradient(#aaa 25%, white 0 50%, #aaa 0 75%, white 0)",
          }}
        />
      }
      options={colors.map(([hex, label]) => ({
        value: allMarks || !selected.length ? hex : nativeColor(hex),
        label,
        color: hex,
      }))}
      onChange={(next) => set("color", next)}
      onDismiss={() => editor.focus()}
    />
  );
  if (compact)
    return (
      <div className="drawing-controls compact" aria-label="Drawing controls">
        {measurement &&
          kind !== "angle" &&
          select("unit", "Display units", ["m", "cm", "mm"])}
        {measurement && (
          <label className="drawing-precision">
            <span>Decimal places</span>
            <select
              aria-label="Decimal places"
              value={String(value("precision"))}
              onChange={(event) => set("precision", Number(event.target.value))}
            >
              <option value="" disabled>
                Mixed
              </option>
              {[0, 1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n ? `0.${"0".repeat(n)}` : "0"}
                </option>
              ))}
            </select>
          </label>
        )}
        {colour}
        {!measurement &&
          (allMarks ? (
            number("weight", "Line weight", 0.5, 12, 0.5)
          ) : (
            <TextStyleSelect
              label={
                kind === "highlight" ? "Highlighter width" : "Stroke width"
              }
              title="Stroke width"
              className="drawing-width"
              value={String(value("size"))}
              options={[
                { value: "s", label: "Thin" },
                { value: "m", label: "Medium" },
                { value: "l", label: "Thick" },
                { value: "xl", label: "Extra thick" },
              ]}
              decoration={<Weight size={17} aria-hidden="true" />}
              onChange={(next) => set("size", next)}
              onDismiss={() => editor.focus()}
            />
          ))}
        {!measurement && kind !== "highlight" && (
          <TextStyleSelect
            label="Line pattern"
            title="Line pattern"
            className="drawing-pattern"
            value={String(value("dash"))}
            options={[
              { value: "solid", label: "Solid" },
              { value: "dashed", label: "Dashed" },
              { value: "dotted", label: "Dotted" },
            ]}
            decoration={
              <span
                className="stroke-sample"
                style={{
                  borderTopStyle:
                    value("dash") === "dotted"
                      ? "dotted"
                      : value("dash") === "dashed"
                        ? "dashed"
                        : "solid",
                }}
              />
            }
            onChange={(next) => set("dash", next)}
            onDismiss={() => editor.focus()}
          />
        )}
      </div>
    );
  return (
    <div className="drawing-controls" aria-label="Drawing controls">
      <label>
        Colour
        <select
          aria-label="Drawing colour"
          value={String(value("color"))}
          onChange={(e) => set("color", e.target.value)}
        >
          <option value="" disabled>
            Mixed / custom
          </option>
          {Object.entries({
            "#d65331": "Terracotta",
            "#25343b": "Charcoal",
            "#517691": "Blue",
            "#657a4c": "Green",
            "#9b6589": "Violet",
          }).map(([c, label]) => (
            <option
              key={c}
              value={allMarks || !selected.length ? c : nativeColor(c)}
            >
              {label}
            </option>
          ))}
        </select>
      </label>
      {allMarks
        ? number("weight", "Line weight", 0.5, 12, 0.5)
        : select(
            "size",
            kind === "highlight" ? "Highlighter width" : "Stroke width",
            ["s", "m", "l", "xl"],
          )}
      {number("opacity", "Opacity (%)", 10, 100, 1, 100)}
      {!compact && (
        <>
          {kind !== "highlight" &&
            select("dash", "Line pattern", ["solid", "dashed", "dotted"])}
          {(!selected.length
            ? ["geo", "cloud"].includes(kind)
            : selected.every((s) => s.type === "geo")) &&
            select("fill", "Fill", ["none", "semi", "solid", "pattern"])}
          {(!selected.length
            ? kind === "arrow"
            : selected.every((s) => s.type === "arrow")) && (
            <>
              {select("start", "Start", ["none", "arrow", "bar", "dot"])}
              {select("end", "End", ["none", "arrow", "bar", "dot"])}
              {select("arrow", "Path", ["arc", "elbow"])}
            </>
          )}
          {area && (
            <>
              {select("hatch", "Area fill", [
                "none",
                "solid",
                "diagonal",
                "cross",
              ])}
              {number("hatchSpacing", "Hatch spacing", 3, 60)}
              {number("hatchAngle", "Hatch angle", 0, 180)}
              <label>
                Fill colour
                <input
                  aria-label="Fill colour"
                  type="color"
                  value={String(value("fillColor") || "#d65331")}
                  onChange={(e) => set("fillColor", e.target.value)}
                />
              </label>
            </>
          )}
          {measurement && (
            <>
              {kind !== "angle" &&
                select("unit", "Display units", ["m", "cm", "mm"])}
              {number("precision", "Decimal places", 0, 4)}
              {number("labelOffset", "Label offset", -200, 200)}
              {kind === "dimension" &&
                select("endStyle", "Dimension ends", [
                  "tick",
                  "bar",
                  "arrow",
                  "none",
                ])}
              {kind === "angle" && (
                <>
                  {number("arcRadius", "Arc radius", 8, 200)}
                  <label>
                    <input
                      type="checkbox"
                      checked={!!value("reflex")}
                      onChange={(e) => set("reflex", e.target.checked)}
                    />
                    Reflex angle
                  </label>
                </>
              )}
            </>
          )}
          {!selected.length && ["line", "dimension"].includes(kind) && (
            <label>
              Exact length ({defaults.unit})
              <input
                aria-label="Exact line length"
                type="number"
                min="0"
                step="any"
                disabled={!source || !sourceMeters(editor, source)}
                value={model.length}
                placeholder="Free drawing"
                onChange={(e) => model.setLength(e.target.value)}
              />
              <small>
                {source
                  ? `${sourceName(source)} · ${sourceMeters(editor, source) ? "scale set" : "set scale to use exact lengths"}`
                  : "Choose a drawing source to set scale."}
              </small>
            </label>
          )}
          {selected.length === 1 &&
            (selected[0].type === "arrow" || selected[0].type === "geo") && (
              <button
                onClick={() => {
                  editor.setEditingShape(selected[0].id);
                  editor.focus();
                }}
              >
                Edit{" "}
                {selected[0].type === "geo" && selected[0].props.geo === "cloud"
                  ? "revision "
                  : ""}
                label
              </button>
            )}
        </>
      )}
    </div>
  );
}
